import { api } from "@lumberset/api-client";
import { MessageWorkspaceButton } from "@lumberset/support-ui";
import { date, dateTime, initials, money, ReadAgain, RunStatus } from "@lumberset/takeoff-ui";
import {
  Avatar,
  Badge,
  Container,
  EmptyState,
  SectionHeader,
  SectionRow,
  Stat,
  Table,
  TwoColumn,
} from "@lumberset/ui";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Crumbs } from "../../../components/crumbs";
import { SubscriptionStatus } from "../../../components/subscription-badge";
import { readAgainRun, yardQuery } from "../../../lib/queries";

export const Route = createFileRoute("/_app/yards/$id")({
  loader: ({ context: { queryClient }, params }) =>
    queryClient.ensureQueryData(yardQuery(params.id)),
  component: YardScreen,
});

/** Better-auth's role names, as the product calls them. */
const memberRoleLabels: Record<string, string> = {
  owner: "Owner",
  admin: "Manager",
  member: "Estimator",
};

function YardScreen() {
  const { id } = Route.useParams();
  const { data } = useSuspenseQuery(yardQuery(id));
  const { yard, members, runs, counts, billing, archivedAt, mergedIntoName } = data;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return (
    <>
      <Crumbs trail={[{ label: "Workspaces", to: "/yards" }, { label: yard.name }]} />

      <Container className="divide-y p-0">
        <SectionHeader
          level="h1"
          title={yard.name}
          description={
            mergedIntoName && archivedAt
              ? `Signed up ${date(yard.createdAt)}. Merged into ${mergedIntoName} on ${date(archivedAt)}: its work is there now.`
              : archivedAt
                ? `Signed up ${date(yard.createdAt)}. Archived by an Admin on ${date(archivedAt)}, so it is out of the lists and the review queue until it is restored.`
                : `Signed up ${date(yard.createdAt)}.`
          }
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <SubscriptionStatus status={billing?.status} />
              <MessageWorkspaceButton
                client={api}
                workspace={{ id: yard.id, name: yard.name }}
                onSent={(ticketId) =>
                  navigate({ to: "/support/tickets/$id", params: { id: ticketId } })
                }
              />
            </div>
          }
        />
        <div className="grid grid-cols-2 divide-ui-border-base max-lg:divide-y lg:grid-cols-4 lg:divide-x">
          <Stat label="Members" value={members.length} />
          <Stat label="Projects" value={counts.projects} />
          <Stat label="Plan sets" value={counts.planSets} />
          <Stat
            label="Takeoff runs"
            value={runs.length}
            hint={runs.length >= 25 ? "The newest 25" : undefined}
          />
        </div>
      </Container>

      <TwoColumn
        main={
          <>
            <Container className="divide-y p-0">
              <SectionHeader
                title="Members"
                description="Everyone who can sign in to this workspace."
              />
              {members.length === 0 ? (
                <EmptyState className="py-10" title="No members" />
              ) : (
                <ul className="divide-y divide-ui-border-base">
                  {members.map((member) => (
                    <li key={member.id} className="flex items-center gap-x-3 px-6 py-3.5">
                      <Avatar fallback={initials(member.name)} shape="circle" />
                      <div className="min-w-0 flex-1">
                        <div className="txt-compact-small-plus truncate text-ui-fg-base">
                          {member.name}
                        </div>
                        <div className="txt-compact-xsmall truncate text-ui-fg-muted">
                          {member.email}
                        </div>
                      </div>
                      <Badge>{memberRoleLabels[member.role] ?? member.role}</Badge>
                    </li>
                  ))}
                </ul>
              )}
            </Container>

            <Container className="divide-y p-0">
              <SectionHeader
                title="Recent takeoff runs"
                description="Newest first. A failed run shows the error the workspace saw, and one that failed or stopped at the limit can be read again."
              />
              {runs.length === 0 ? (
                <EmptyState
                  className="py-10"
                  title="No takeoff runs yet"
                  description="Runs appear here once the workspace uploads drawings."
                />
              ) : (
                <Table>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>Status</Table.HeaderCell>
                      <Table.HeaderCell>Project</Table.HeaderCell>
                      <Table.HeaderCell className="max-md:hidden">Started</Table.HeaderCell>
                      <Table.HeaderCell>What happened</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {runs.map((run) => (
                      <Table.Row key={run.id}>
                        <Table.Cell className="whitespace-nowrap">
                          <RunStatus status={run.status} />
                        </Table.Cell>
                        <Table.Cell className="txt-compact-small-plus whitespace-nowrap text-ui-fg-base">
                          {run.projectName}
                        </Table.Cell>
                        <Table.Cell className="whitespace-nowrap max-md:hidden">
                          {dateTime(run.createdAt)}
                        </Table.Cell>
                        <Table.Cell className="min-w-[200px] text-pretty py-3">
                          {run.error ? (
                            <span className="text-ui-fg-error">{run.error}</span>
                          ) : run.status === "in_review" && run.heldFor === "reading_stopped" ? (
                            <span className="text-ui-tag-orange-text">
                              Reading stopped at the limit
                            </span>
                          ) : (
                            <span className="text-ui-fg-muted">-</span>
                          )}
                          {run.readAgain ? (
                            <ReadAgain
                              className="mt-1.5 flex"
                              offer={run.readAgain}
                              projectName={run.projectName}
                              send={() => readAgainRun(run.id)}
                              onSent={() =>
                                Promise.all([
                                  queryClient.invalidateQueries({ queryKey: ["yards", id] }),
                                  queryClient.invalidateQueries({ queryKey: ["takeoffs"] }),
                                  queryClient.invalidateQueries({ queryKey: ["reviews"] }),
                                ])
                              }
                            />
                          ) : null}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              )}
            </Container>
          </>
        }
        side={
          <Container className="divide-y p-0">
            <SectionHeader
              title="Billing"
              description="What this workspace pays when a takeoff is released to it."
            />
            {!billing ? (
              <EmptyState
                className="py-10"
                title="No billing summary"
                description="This workspace has no subscription on record. An Admin can put it on a Billing Plan."
              />
            ) : (
              <>
                <SectionRow label="Billing plan">{billing.billingPlan.name}</SectionRow>
                <SectionRow label="Status">
                  <SubscriptionStatus status={billing.status} />
                </SectionRow>
                <SectionRow label="Quote fee">
                  {money(billing.billingPlan.quoteFeeCents)}
                </SectionRow>
                <SectionRow label="Monthly fee">
                  {billing.billingPlan.monthlyFeeCents > 0
                    ? `${money(billing.billingPlan.monthlyFeeCents)}, includes ${billing.billingPlan.includedQuotes} quotes`
                    : "None"}
                </SectionRow>
                <SectionRow label="Trial quotes left">{billing.trialQuotesRemaining}</SectionRow>
                {billing.accountCreditCents > 0 ? (
                  <SectionRow label="Account credit">
                    {money(billing.accountCreditCents)}, used before the card
                  </SectionRow>
                ) : null}
                <SectionRow label="Quotes this period">{billing.quotesThisPeriod}</SectionRow>
                <SectionRow label="Period ends">
                  {billing.periodEnd ? date(billing.periodEnd) : "-"}
                </SectionRow>
                <SectionRow label="Card on file">
                  {billing.hasPaymentMethod ? "Yes" : "No"}
                </SectionRow>
                <SectionRow label="Next quote">
                  {billing.nextQuote.basis === "trial"
                    ? "Free, uses a trial quote"
                    : billing.nextQuote.basis === "included"
                      ? "Included in the monthly fee"
                      : billing.nextQuote.basis === "credit"
                        ? "Free, uses a credit"
                        : billing.nextQuote.fromAccountCreditCents > 0
                          ? `${money(billing.nextQuote.amountCents)} to the card, after ${money(billing.nextQuote.fromAccountCreditCents)} of account credit`
                          : money(billing.nextQuote.amountCents)}
                </SectionRow>
                <SectionRow label="Payments">
                  {billing.paymentsLive ? "Live" : "Not live, nothing is charged"}
                </SectionRow>
              </>
            )}
          </Container>
        }
      />
    </>
  );
}
